import {
  COMMON_NUTRITION_LABELS,
  defaultUnitForNutritionLabel,
  type NutritionBasis,
  type NutritionFact,
  type NutritionFactPatch,
  type SourceType,
} from '@platewell/core';
import {
  parseListContext,
  useCollections,
  useConnectionStatus,
  useRecipe,
  usePlatewell,
  useStore,
} from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { AddCollectionDialog } from '../components/add-collection-dialog.js';
import { backToListTarget } from '../components/back-to-list-link.js';
import { Photo } from '../components/photo.js';
import { TextField } from '../components/text-field.js';
import { useShowToast } from '../components/toast.js';
import { quotaRefusal } from '../components/upgrade-notice.js';
import { attachPhoto } from '../lib/uploads.js';
import { recipeEditRoute } from '../router.js';

// Long enough to notice the recipe has gone from the list and reach for Undo.
const UNDO_TIMEOUT_MS = 30_000;

export function RecipeEdit() {
  const { recipeId } = recipeEditRoute.useParams();
  // Carried through the edit so Done lands on a recipe that still knows which list it is in.
  const search = recipeEditRoute.useSearch();
  const recipe = useRecipe(recipeId);
  const collections = useCollections();
  const store = useStore();
  const { activeHouseholdId } = usePlatewell();
  const { readOnly } = useConnectionStatus();
  const navigate = useNavigate();
  const showToast = useShowToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [tagDraft, setTagDraft] = useState('');
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [addingCollection, setAddingCollection] = useState(false);

  if (recipe == null) {
    return (
      <div className="empty">
        <h2>Recipe not found</h2>
        <Link className="btn" to="/">
          Back to all recipes
        </Link>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="empty">
        <h2>View only</h2>
        <p className="hint">You have read access to this household, so editing is turned off.</p>
      </div>
    );
  }

  async function addPhotos(files: FileList | null) {
    if (files == null || activeHouseholdId == null) return;
    setPhotoError(null);
    for (const file of Array.from(files)) {
      try {
        await attachPhoto(store, recipeId, file, activeHouseholdId);
      } catch (cause) {
        setPhotoError(cause instanceof Error ? cause.message : 'Could not add that photo');
      }
    }
  }

  function commitTags(raw: string) {
    void store.setTags(
      recipeId,
      raw
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag !== ''),
    );
  }

  return (
    <div className="editor">
      <div className="row">
        <h1>Edit recipe</h1>
        <span className="spacer" />
        <span className="hint">Saved as you type</span>
        <Link
          className="btn primary"
          to="/recipes/$recipeId"
          params={{ recipeId }}
          search={search}
        >
          Done
        </Link>
      </div>

      <TextField
        label="Title"
        value={recipe.title}
        autoFocus={recipe.title === ''}
        placeholder="Sunday roast chicken"
        onCommit={(title) => void store.updateRecipe(recipeId, { title })}
      />

      <TextField
        label="Description"
        multiline
        value={recipe.description}
        placeholder="A short note about why you like it"
        onCommit={(description) => void store.updateRecipe(recipeId, { description })}
      />

      <section>
        <span className="label">Photos</span>
        <div className="photo-strip" style={{ marginTop: 8 }}>
          {recipe.photos.map((photo) => (
            <div className="photo-thumb" key={photo.id}>
              <Photo blobId={photo.blobId} alt="" size={400} />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => void store.removePhoto(recipeId, photo.id)}
              >
                ×
              </button>
            </div>
          ))}
          <button className="btn" type="button" onClick={() => fileInput.current?.click()}>
            Add photo
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(event) => {
              void addPhotos(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
        {photoError != null && <p className="error-text">{photoError}</p>}
        <p className="hint" style={{ marginTop: 6 }}>
          Photos are added straight away and upload in the background — taking one with no signal
          is fine.
        </p>
      </section>

      <section>
        <span className="label">Ingredients</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {recipe.ingredients.map((ingredient, index) => (
            <div className="line-item" key={ingredient.id}>
              <TextField
                value={ingredient.quantity}
                placeholder="250"
                onCommit={(quantity) =>
                  void store.updateIngredient(recipeId, ingredient.id, { quantity })
                }
              />
              <TextField
                value={ingredient.unit}
                placeholder="g"
                autoCapitalize="none"
                onCommit={(unit) => void store.updateIngredient(recipeId, ingredient.id, { unit })}
              />
              <TextField
                value={ingredient.name}
                placeholder="ricotta"
                autoCapitalize="none"
                onCommit={(name) => void store.updateIngredient(recipeId, ingredient.id, { name })}
              />
              <TextField
                value={ingredient.note}
                placeholder="drained, room temp"
                autoCapitalize="none"
                onCommit={(note) => void store.updateIngredient(recipeId, ingredient.id, { note })}
              />
              <div className="row">
                <button
                  className="drag"
                  type="button"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => void store.moveIngredient(recipeId, ingredient.id, index - 1)}
                >
                  ↑
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Move down"
                  disabled={index === recipe.ingredients.length - 1}
                  onClick={() => void store.moveIngredient(recipeId, ingredient.id, index + 1)}
                >
                  ↓
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Remove ingredient"
                  onClick={() => void store.removeIngredient(recipeId, ingredient.id)}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          className="btn"
          type="button"
          style={{ marginTop: 10 }}
          onClick={() => void store.addIngredient(recipeId, { name: '' })}
        >
          Add ingredient
        </button>
      </section>

      <section>
        <span className="label">Method</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          {recipe.instructions.map((instruction, index) => (
            <div className="row" key={instruction.id} style={{ alignItems: 'flex-start' }}>
              <span className="hint" style={{ paddingTop: 10, minWidth: 20 }}>
                {index + 1}.
              </span>
              <TextField
                multiline
                value={instruction.text}
                placeholder="Describe the step"
                onCommit={(text) => void store.updateInstruction(recipeId, instruction.id, { text })}
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                  className="drag"
                  type="button"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => void store.moveInstruction(recipeId, instruction.id, index - 1)}
                >
                  ↑
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Move down"
                  disabled={index === recipe.instructions.length - 1}
                  onClick={() => void store.moveInstruction(recipeId, instruction.id, index + 1)}
                >
                  ↓
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Remove step"
                  onClick={() => void store.removeInstruction(recipeId, instruction.id)}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          className="btn"
          type="button"
          style={{ marginTop: 10 }}
          onClick={() => void store.addInstruction(recipeId, { text: '' })}
        >
          Add step
        </button>
      </section>

      <section className="row wrap" style={{ gap: 14 }}>
        <TextField
          label="Servings"
          value={recipe.servings}
          placeholder="4"
          onCommit={(servings) => void store.updateRecipe(recipeId, { servings })}
        />
        <TextField
          label="Prep (minutes)"
          type="number"
          value={recipe.prepMinutes?.toString() ?? ''}
          onCommit={(value) =>
            void store.updateRecipe(recipeId, { prepMinutes: value === '' ? null : Number(value) })
          }
        />
        <TextField
          label="Cook (minutes)"
          type="number"
          value={recipe.cookMinutes?.toString() ?? ''}
          onCommit={(value) =>
            void store.updateRecipe(recipeId, { cookMinutes: value === '' ? null : Number(value) })
          }
        />
      </section>

      <section>
        <span className="label">Nutrition</span>
        <div className="field" style={{ marginTop: 8 }}>
          <span className="label">Amounts are</span>
          <select
            className="select"
            style={{ maxWidth: 200 }}
            value={recipe.nutritionBasis}
            onChange={(event) =>
              void store.updateRecipe(recipeId, {
                nutritionBasis: event.target.value as NutritionBasis,
              })
            }
          >
            <option value="unknown">Unknown</option>
            <option value="perServing">Per serving</option>
            <option value="perRecipe">Per recipe</option>
          </select>
        </div>
        <datalist id="nutrition-labels">
          {COMMON_NUTRITION_LABELS.map((item) => (
            <option key={item.label} value={item.label} />
          ))}
        </datalist>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {recipe.nutritionFacts.map((fact, index) => (
            <div className="nutrition-item" key={fact.id}>
              <TextField
                value={fact.label}
                placeholder="Protein"
                list="nutrition-labels"
                onCommit={(label) =>
                  void store.updateNutritionFact(
                    recipeId,
                    fact.id,
                    nutritionLabelPatch(fact, label),
                  )
                }
              />
              <TextField
                value={fact.amount}
                placeholder="12"
                onCommit={(amount) =>
                  void store.updateNutritionFact(recipeId, fact.id, { amount })
                }
              />
              <TextField
                value={fact.unit}
                placeholder="g"
                onCommit={(unit) => void store.updateNutritionFact(recipeId, fact.id, { unit })}
              />
              <div className="row">
                <button
                  className="drag"
                  type="button"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => void store.moveNutritionFact(recipeId, fact.id, index - 1)}
                >
                  ↑
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Move down"
                  disabled={index === recipe.nutritionFacts.length - 1}
                  onClick={() => void store.moveNutritionFact(recipeId, fact.id, index + 1)}
                >
                  ↓
                </button>
                <button
                  className="drag"
                  type="button"
                  aria-label="Remove nutrition fact"
                  onClick={() => void store.removeNutritionFact(recipeId, fact.id)}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          className="btn"
          type="button"
          style={{ marginTop: 10 }}
          onClick={() => void store.addNutritionFact(recipeId, { label: '' })}
        >
          Add nutrient
        </button>
      </section>

      <section className="field">
        <span className="label">Source</span>
        <div className="row">
          <select
            className="select"
            style={{ maxWidth: 140 }}
            value={recipe.sourceType}
            onChange={(event) =>
              void store.updateRecipe(recipeId, { sourceType: event.target.value as SourceType })
            }
          >
            <option value="none">None</option>
            <option value="url">Link</option>
            <option value="text">Text</option>
          </select>
          {recipe.sourceType === 'url' && (
            <TextField
              value={recipe.sourceUrl}
              placeholder="https://…"
              onCommit={(sourceUrl) => void store.updateRecipe(recipeId, { sourceUrl })}
            />
          )}
          {recipe.sourceType === 'text' && (
            <TextField
              value={recipe.sourceText}
              placeholder="Grandma's index card"
              onCommit={(sourceText) => void store.updateRecipe(recipeId, { sourceText })}
            />
          )}
        </div>
      </section>

      <TextField
        label="Notes"
        multiline
        value={recipe.notes}
        placeholder="What you would change next time"
        onCommit={(notes) => void store.updateRecipe(recipeId, { notes })}
      />

      <section className="field">
        <span className="label">Tags</span>
        <input
          className="input"
          value={tagDraft === '' ? recipe.tags.join(', ') : tagDraft}
          placeholder="weeknight, vegetarian"
          onChange={(event) => setTagDraft(event.target.value)}
          onBlur={() => {
            if (tagDraft !== '') commitTags(tagDraft);
            setTagDraft('');
          }}
        />
      </section>

      <section className="field">
        <div className="row">
          <span className="label">Collections</span>
          <span className="spacer" />
          <button
            type="button"
            className="btn ghost small"
            onClick={() => setAddingCollection(true)}
          >
            Add
          </button>
        </div>
        <div className="row wrap">
          {collections.map((collection) => {
            const member = recipe.collectionIds.includes(collection.id);
            return (
              <button
                key={collection.id}
                type="button"
                className={`btn ${member ? 'primary' : ''}`}
                onClick={() =>
                  void (member
                    ? store.removeRecipeFromCollection(recipeId, collection.id)
                    : store.addRecipeToCollection(recipeId, collection.id))
                }
              >
                {collection.name}
              </button>
            );
          })}
        </div>
        {addingCollection && (
          <AddCollectionDialog
            existingNames={collections.map((collection) => collection.name)}
            onClose={() => setAddingCollection(false)}
            onCreate={async (name) => {
              // Created from a recipe, so the recipe goes straight into it.
              const id = await store.createCollection({ name });
              await store.addRecipeToCollection(recipeId, id);
              setAddingCollection(false);
            }}
          />
        )}
      </section>

      <div className="row">
        <button
          className="btn danger"
          type="button"
          onClick={() => {
            // Trashing from inside a list drops back into that list, so working through a
            // search and binning the duds does not eject you to All Recipes each time.
            const context = parseListContext(search);
            void store.deleteRecipe(recipeId).then(() => {
              // Trashing keeps the recipe's collection memberships, so restoring puts it
              // back exactly where it was.
              showToast('Recipe moved to the trash', {
                timeoutMs: UNDO_TIMEOUT_MS,
                action: {
                  label: 'Undo',
                  onClick: () => {
                    store.restoreRecipe(recipeId).catch((cause: unknown) => {
                      showToast(
                        quotaRefusal(cause) != null
                          ? 'Could not undo: your household is at its recipe limit'
                          : 'Could not undo: the recipe is still in the trash',
                      );
                    });
                  },
                },
              });
              return navigate(context == null ? { to: '/' } : backToListTarget(context));
            });
          }}
        >
          Move to trash
        </button>
        <span className="spacer" />
        <span className="hint">Saved as you type</span>
        <Link
          className="btn primary"
          to="/recipes/$recipeId"
          params={{ recipeId }}
          search={search}
        >
          Done
        </Link>
      </div>
    </div>
  );
}

function nutritionLabelPatch(fact: NutritionFact, label: string): NutritionFactPatch {
  const patch: NutritionFactPatch = { label };
  if (fact.unit === '') {
    const unit = defaultUnitForNutritionLabel(label);
    if (unit !== '') return { ...patch, unit };
  }
  return patch;
}
